import React, { useCallback, useEffect, useState } from "react";
import { Convert, ReservationElement as Reservation } from "@/models/responses";
import { GridDropdownFilter } from "@/lib/organisms/Grid";
import ReservationsPage, { ReservationRow } from "./reservations";
import { PeriodName, Platform } from "@/models";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { GridApi } from "ag-grid-community";

export const UserReservationsPage: React.FC = () => {
    const [reservations, setReservations] = useState<ReservationRow[]>([]);
    const [, setGridApi] = useState<GridApi | null>(null);

    const { selectedPeriod, selectedYear, ownerOptions, periodOptions, yearOptions, selectedOwner } = useAppData();
    const apartmentEntities = useActiveApartmentEntities();

    const { setSelectedPeriod, setSelectedYear, setSelectedOwner } = useUserSettingsDispatch();

    const getAndSetReservations = useCallback(
        (year: string, period: string): void => {
            let url;
            url = `${apiUrl}${getApiBasePath()}/owners/${selectedOwner}/reservations?year=${year}&period=${period}`;
            fetch(url, fetchOptions)
                .then((response) => {
                    if (response) {
                        return response.text();
                    }
                    throw new Error("Unauthorized");
                })
                .then((response) => {
                    let responseJson = Convert.toGetReservationsResponse(response);
                    let res = responseJson.reservations.map((reservation: Reservation) => {
                        return {
                            id: reservation.id,
                            apartment: apartmentEntities[reservation.apartmentId]?.name || "N/A",
                            apartmentStr: apartmentEntities[reservation.apartmentId]?.shortTermRegistryId || "N/A",
                            strApartmentID: apartmentEntities[reservation.apartmentId].shortTermPropertyId || 0,
                            platform: Platform[reservation.platform],
                            platformEnum: reservation.platform,
                            guestId: reservation.guestId || "",
                            reservationCode: reservation.reservationCode,
                            bookingDate: reservation.bookingDate,
                            startDate: reservation.startDate,
                            endDate: reservation.endDate,
                            actualDaysOfStay: reservation.actualDaysOfStay,
                            guestName: reservation.guestName,
                            numberOfGuests: reservation.numberOfGuests,
                            receivableIncome: reservation.receivableIncome,
                            amountPaid: reservation.amountPaid,
                            paymentDate: reservation.paymentDate,
                            commission: reservation.commission,
                            cleaningFee: reservation.cleaningFee,
                            cleaningCost: reservation.cleaningCost,
                            hostingExpense: reservation.hostingExpense,
                            epsilon: reservation.epsilon,
                            netIncome: reservation.netIncome,
                            grossAdr: reservation.grossAdr,
                            netAdr: reservation.netAdr,
                            vat: reservation.vat,
                            sustainabilityFee: reservation.sustainabilityFee,
                            temporaryStayDuty: reservation.temporaryStayDuty,
                        };
                    });
                    setReservations(res);
                })
                .catch((error) => console.log(error));
        },
        [apartmentEntities, selectedOwner],
    );

    useEffect(() => {
        getAndSetReservations(selectedYear.toString(), selectedPeriod);
    }, [selectedPeriod, selectedYear, apartmentEntities, getAndSetReservations]);

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
        {
            label: "Owner",
            options: ownerOptions,
            selectedOption: selectedOwner.toString(),
            onSelectionChanged: (owner) => setSelectedOwner(Number(owner)),
        },
    ];

    return <ReservationsPage reservations={reservations} onGridReady={setGridApi} gridDropdownFilters={gridDropdownFilters} />;
};
